/**
 * Which operator actions ask first, and how firmly (PLAN §5.3, D3). The
 * playout screen's actions, by what they can do to air:
 *
 *   Take                       puts the next part on air — the operator's job;
 *                              NEVER confirmed. Two takes at once are refused by
 *                              the take guard (`hotkeys.ts`: pending, too soon),
 *                              and a take that re-airs an earlier part is
 *                              flagged on the button (`backwardTake`), not asked.
 *   next ±1, set next, set /   change only what is next: nothing airs until a
 *   queue a segment, skip the  take, so they act at once
 *   next piece
 *   adlib / action (panel      the operator's deliberate on-air tools, often on
 *   item, hotkey, free form)   a hotkey in a hurry: act at once
 *   "Update on air" (D19)      its banner IS the confirmation D19 asks for
 *   activate on air            makes the playlist live: confirmed
 *   activate for rehearsal     confirmed (it replaces whatever activation there is)
 *   deactivate                 stops playout: confirmed; FIRMLY while a part is
 *                              on air for real (not rehearsal)
 *   reset                      rewinds to the top, starts the as-played record
 *                              over: confirmed; FIRMLY while a part is on air
 *   stop a source layer        cuts what plays on it: confirmed while something
 *                              is playing there, at once when nothing is
 *   switch over (main/backup)  moves device control to the other node mid-show:
 *                              confirmed; FIRMLY while a part is on air
 *
 * FIRMLY (`guarded`) means the dialog opens with Cancel focused, so a
 * reflexive Enter — the key that also takes — cancels instead of confirming.
 */

export type GuardedAction =
  | { readonly kind: 'activate' }
  | { readonly kind: 'rehearsal' }
  | { readonly kind: 'deactivate' }
  | { readonly kind: 'reset' }
  | { readonly kind: 'switchover'; readonly toNode: string }
  | { readonly kind: 'stop-layer'; readonly layer: string; readonly layerLabel: string };

export type SafetyContext = {
  /** The on-air part's title, or null when nothing is on air. */
  readonly onAirTitle: string | null;
  /** The playlist is activated for rehearsal (nothing airs for real). */
  readonly rehearsal: boolean;
  /** The source layers something is playing on right now. */
  readonly layersPlaying: ReadonlySet<string>;
};

export type Confirmation = {
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  /** `take`: puts something on air; `destructive`: stops or discards something. */
  readonly tone: 'take' | 'destructive' | 'default';
  /** Cancel is focused: a stray Enter does nothing. */
  readonly guarded: boolean;
};

function liveNow(ctx: SafetyContext): string | null {
  return ctx.onAirTitle !== null && !ctx.rehearsal ? ctx.onAirTitle : null;
}

/** The confirmation an action needs, or null: it acts at once. */
export function confirmationFor(action: GuardedAction, ctx: SafetyContext): Confirmation | null {
  const live = liveNow(ctx);
  switch (action.kind) {
    case 'activate':
      return { title: 'Activate on air?', description: 'The playlist goes live: takes put its parts on air for real, and the devices follow.', confirmLabel: 'Activate on air', tone: 'take', guarded: false };
    case 'rehearsal':
      return { title: 'Activate for rehearsal?', description: 'Playout runs as it would on air, but nothing is aired for real.', confirmLabel: 'Activate rehearsal', tone: 'default', guarded: false };
    case 'deactivate':
      return live === null
        ? { title: 'Deactivate?', description: 'Playout stops: nothing is on air or next until the playlist is activated again.', confirmLabel: 'Deactivate', tone: 'destructive', guarded: false }
        : {
            title: 'Deactivate while on air?',
            description: `“${live}” is on air. Deactivating stops playout now: the devices stop following the rundown, and nothing is on air or next until the playlist is activated again.`,
            confirmLabel: 'Deactivate now',
            tone: 'destructive',
            guarded: true,
          };
    case 'reset':
      return live === null
        ? { title: 'Reset to the top?', description: 'A fresh activation with nothing played: the first part becomes next, and the as-played record starts over.', confirmLabel: 'Reset', tone: 'destructive', guarded: false }
        : {
            title: 'Reset while on air?',
            description: `“${live}” is on air. Resetting rewinds the running order to the top: the first part becomes next, and the as-played record starts over.`,
            confirmLabel: 'Reset now',
            tone: 'destructive',
            guarded: true,
          };
    case 'switchover':
      return {
        title: 'Switch over?',
        description: `Device control moves to ${action.toNode}${live === null ? '' : ` while “${live}” is on air`}. The devices keep their state; the other node takes the next command.`,
        confirmLabel: 'Switch over',
        tone: 'default',
        guarded: live !== null,
      };
    case 'stop-layer':
      return ctx.layersPlaying.has(action.layer)
        ? { title: `Stop ${action.layerLabel}?`, description: `What plays on ${action.layerLabel} stops now${live === null ? '' : ', on air'}.`, confirmLabel: `Stop ${action.layerLabel}`, tone: 'destructive', guarded: false }
        : null;
  }
}

/** The source layers with a piece playing at the production's `now`: started, and not yet ended. */
export function layersPlaying(pieces: readonly { readonly sourceLayer: string; readonly startsInMs: number; readonly remainingMs: number | null }[]): ReadonlySet<string> {
  return new Set(pieces.filter((p) => p.startsInMs <= 0 && (p.remainingMs === null || p.remainingMs > 0)).map((p) => p.sourceLayer));
}

/**
 * Whether taking would put an EARLIER part of the running order on air than
 * the one on air now (next was set back: a re-air, not the show moving on).
 * Worth saying on the TAKE button; never worth a dialog.
 */
export function backwardTake(partOrder: readonly string[], onAirPartId: string | null, nextPartId: string | null): boolean {
  if (onAirPartId === null || nextPartId === null) return false;
  const onAir = partOrder.indexOf(onAirPartId);
  const next = partOrder.indexOf(nextPartId);
  return onAir >= 0 && next >= 0 && next <= onAir;
}
